import store from "../store";
import axios from "axios";
import { userManagementActions } from "../reducers/userManagement";
import { getAccessToken } from "./auth";
import API_BASE_URL from '../../config/baseUrl';

const { dispatch } = store;


// User Type mapping
export const USER_TYPES = {
  CLIENT: 1,
  AGENT: 2,
  SUPERMASTER: 3,
  SUPER: 3,
  MASTER: 4,
  MINIADMIN: 5,
  ADMIN: 6,
  SUPERADMIN: 7,
};

export const USER_TYPE_NAMES = {
1: 'Client', 2: 'Agent', 3: 'Super', 4: 'Master', 5: 'MiniAdmin', 6: 'Admin', 7: 'SuperAdmin', };

// Fetch Users List
export const fetchUsers = async function(userType, parentId, page, noOfRecords) {
  dispatch(userManagementActions.fetchUsersStart());

  try {
    const token = getAccessToken();
    
    if (!token) {
      dispatch(userManagementActions.fetchUsersFail("No access token found"));
      return { success: false };
    }

    const response = await axios.post(
      API_BASE_URL + "/user/list-user-v2",
      {
        userType: String(userType),
        parentId: parentId || "",
        noOfRecords: noOfRecords || 250,
        index: page || 0,
        forDeadClient: false,
      },
      {
        headers: { Authorization: "Bearer " + token },
      }
    );

    const data = response.data;

    if (data.status === false) {
      dispatch(userManagementActions.fetchUsersFail(data.message || "Failed to fetch users"));
      return { success: false, error: data.message };
    }

    dispatch(userManagementActions.fetchUsersSuccess({
      userList: data.data.userListV2 || [],
      totalPages: data.data.totalPages || 1,
      currentPage: data.data.currentPage || 0,
    }));

    return { success: true, data: data.data };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to fetch users";
    dispatch(userManagementActions.fetchUsersFail(errorMessage));
    return { success: false, error: errorMessage };
  }
};

// Create User
export const createUser = async function(userData) {
  dispatch(userManagementActions.createUserStart());

  try {
    const token = getAccessToken();
    
    if (!token) {
      dispatch(userManagementActions.createUserFail("No access token found"));
      return { success: false };
    }

    const response = await axios.post(
      API_BASE_URL + "/user/create",
      userData,
      {
        headers: { Authorization: "Bearer " + token },
      }
    );

    const data = response.data;

    if (data.status === false) {
      dispatch(userManagementActions.createUserFail(data.message || "Failed to create user"));
      return { success: false, error: data.message };
    }

    dispatch(userManagementActions.createUserSuccess(data.data));
    return { success: true, data: data.data };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to create user";
    dispatch(userManagementActions.createUserFail(errorMessage));
    return { success: false, error: errorMessage };
  }
};

// Update User
export const updateUser = async function(userData) {
  dispatch(userManagementActions.updateUserStart());

  try {
    const token = getAccessToken();
    
    if (!token) {
      dispatch(userManagementActions.updateUserFail("No access token found"));
      return { success: false };
    }

    const response = await axios.post(
      API_BASE_URL + "/user/update-detail",
      userData,
      {
        headers: { Authorization: "Bearer " + token },
      }
    );

    const data = response.data;

    if (data.status === false) {
      dispatch(userManagementActions.updateUserFail(data.message || "Failed to update user"));
      return { success: false, error: data.message };
    }

    dispatch(userManagementActions.updateUserSuccess({ userId: userData.userId, ...userData }));
    return { success: true };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to update user";
    dispatch(userManagementActions.updateUserFail(errorMessage));
    return { success: false, error: errorMessage };
  }
};

// Fetch Parent Share (Partnership Details)
export const fetchParentShare = async function(userId) {
  dispatch(userManagementActions.fetchParentShareStart());

  try {
    const token = getAccessToken();
    
    if (!token) {
      dispatch(userManagementActions.fetchParentShareFail("No access token found"));
      return { success: false };
    }

    const response = await axios.post(
      API_BASE_URL + "/user/user-parent-share",
      { userId: userId },
      {
        headers: { Authorization: "Bearer " + token },
      }
    );

    const data = response.data;

    if (data.status === false) {
      dispatch(userManagementActions.fetchParentShareFail(data.message || "Failed to fetch partnership"));
      return { success: false, error: data.message };
    }

    dispatch(userManagementActions.fetchParentShareSuccess(data.data));
    return { success: true, data: data.data };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to fetch partnership";
    dispatch(userManagementActions.fetchParentShareFail(errorMessage));
    return { success: false, error: errorMessage };
  }
};

// Deposit
export const depositToUser = async function(userId, amount) {
  dispatch(userManagementActions.userActionStart());

  try {
    const token = getAccessToken();
    
    if (!token) {
      dispatch(userManagementActions.userActionFail("No access token found"));
      return { success: false };
    }

    const response = await axios.post(
      API_BASE_URL + "/user/deposit",
      { userId: userId, amount: amount },
      {
        headers: { Authorization: "Bearer " + token },
      }
    );

    const data = response.data;

    if (data.status === false) {
      dispatch(userManagementActions.userActionFail(data.message || "Deposit failed"));
      return { success: false, error: data.message };
    }

    dispatch(userManagementActions.userActionSuccess({ 
      userId: userId, 
      updates: { balance: data.data?.newBalance } 
    }));
    return { success: true, data: data.data };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Deposit failed";
    dispatch(userManagementActions.userActionFail(errorMessage));
    return { success: false, error: errorMessage };
  }
};

// Withdraw
export const withdrawFromUser = async function(userId, amount) {
  dispatch(userManagementActions.userActionStart());

  try {
    const token = getAccessToken();
    
    if (!token) {
      dispatch(userManagementActions.userActionFail("No access token found"));
      return { success: false };
    }

    const response = await axios.post(
      API_BASE_URL + "/user/withdraw",
      { userId: userId, amount: amount },
      {
        headers: { Authorization: "Bearer " + token },
      }
    );

    const data = response.data;

    if (data.status === false) {
      dispatch(userManagementActions.userActionFail(data.message || "Withdrawal failed"));
      return { success: false, error: data.message };
    }

    dispatch(userManagementActions.userActionSuccess({ 
      userId: userId, 
      updates: { balance: data.data?.newBalance } 
    }));
    return { success: true, data: data.data };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Withdrawal failed";
    dispatch(userManagementActions.userActionFail(errorMessage));
    return { success: false, error: errorMessage };
  }
};

// Toggle User Status (Active/Inactive)
export const toggleUserStatus = async function(userId, isActive) {
  dispatch(userManagementActions.userActionStart());

  try {
    const token = getAccessToken();
    
    if (!token) {
      dispatch(userManagementActions.userActionFail("No access token found"));
      return { success: false };
    }

    const response = await axios.post(
      API_BASE_URL + "/user/update-status",
      { userId: userId, isActive: isActive },
      {
        headers: { Authorization: "Bearer " + token },
      }
    );

    const data = response.data;

    if (data.status === false) {
      dispatch(userManagementActions.userActionFail(data.message || "Failed to update status"));
      return { success: false, error: data.message };
    }

    dispatch(userManagementActions.userActionSuccess({ 
      userId: userId, 
      updates: { isActive: isActive } 
    }));
    return { success: true };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to update status";
    dispatch(userManagementActions.userActionFail(errorMessage));
    return { success: false, error: errorMessage };
  }
};

// Toggle Bet Lock
export const toggleBetLock = async function(userId, betLock) {
  dispatch(userManagementActions.userActionStart());

  try {
    const token = getAccessToken();
    
    if (!token) {
      dispatch(userManagementActions.userActionFail("No access token found"));
      return { success: false };
    }

    const response = await axios.post(
      API_BASE_URL + "/user/update-bet-lock",
      { userId: userId, betLock: betLock },
      {
        headers: { Authorization: "Bearer " + token },
      }
    );

    const data = response.data;

    if (data.status === false) {
      dispatch(userManagementActions.userActionFail(data.message || "Failed to update bet lock"));
      return { success: false, error: data.message };
    }

    dispatch(userManagementActions.userActionSuccess({ 
      userId: userId, 
      updates: { betLock: betLock } 
    }));
    return { success: true };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to update bet lock";
    dispatch(userManagementActions.userActionFail(errorMessage));
    return { success: false, error: errorMessage };
  }
};

// Toggle Casino Lock
export const toggleCasinoLock = async function(userId, casinoLock) {
  dispatch(userManagementActions.userActionStart());

  try {
    const token = getAccessToken();
    
    if (!token) {
      dispatch(userManagementActions.userActionFail("No access token found"));
      return { success: false };
    }

    const response = await axios.post(
      API_BASE_URL + "/user/update-casino-lock",
      { userId: userId, casinoLock: casinoLock },
      {
        headers: { Authorization: "Bearer " + token },
      }
    );

    const data = response.data;

    if (data.status === false) {
      dispatch(userManagementActions.userActionFail(data.message || "Failed to update casino lock"));
      return { success: false, error: data.message };
    }

    dispatch(userManagementActions.userActionSuccess({ 
      userId: userId, 
      updates: { casinoLock: casinoLock } 
    }));
    return { success: true };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to update casino lock";
    dispatch(userManagementActions.userActionFail(errorMessage));
    return { success: false, error: errorMessage };
  }
};

// Toggle Matka Lock
export const toggleMatkaLock = async function(userId, matkaLock) {
  dispatch(userManagementActions.userActionStart());

  try {
    const token = getAccessToken();
    
    if (!token) {
      dispatch(userManagementActions.userActionFail("No access token found"));
      return { success: false };
    }

    const response = await axios.post(
      API_BASE_URL + "/ant-pro/matka-bet-lock",
      { userId: userId, isLock: matkaLock },
      {
        headers: { Authorization: "Bearer " + token },
      }
    );

    const data = response.data;

    if (data.status === false) {
      dispatch(userManagementActions.userActionFail(data.message || "Failed to update matka lock"));
      return { success: false, error: data.message };
    }

    dispatch(userManagementActions.userActionSuccess({ 
      userId: userId, 
      updates: { matkaBetLock: matkaLock } 
    }));
    return { success: true, message: data.message };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to update matka lock";
    dispatch(userManagementActions.userActionFail(errorMessage));
    return { success: false, error: errorMessage };
  }
};

// Reset Password
export const resetPassword = async function(userId, newPassword) {
  dispatch(userManagementActions.userActionStart());

  try {
    const response = await axios.post(
      API_BASE_URL + "/user/reset-password",
      { userId: userId, password: newPassword }
    );

    const data = response.data;

    if (data.status === false) {
      dispatch(userManagementActions.userActionFail(data.message || "Failed to reset password"));
      return { success: false, error: data.message };
    }

    dispatch(userManagementActions.userActionSuccess({ userId: userId, updates: {} }));
    return { success: true };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to reset password";
    dispatch(userManagementActions.userActionFail(errorMessage));
    return { success: false, error: errorMessage };
  }
};

// Clear functions
export const clearCreateResult = function() {
  dispatch(userManagementActions.clearCreateResult());
};

export const clearParentShare = function() {
  dispatch(userManagementActions.clearParentShare());
};

export const setSelectedUser = function(user) {
  dispatch(userManagementActions.setSelectedUser(user));
};

export const clearSelectedUser = function() {
  dispatch(userManagementActions.clearSelectedUser());
};

export const resetUserManagement = function() {
  dispatch(userManagementActions.resetUserManagement());
};
